import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap, ArrowRight, CheckCircle2, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

function Toggle({ on, onChange, testid }) {
  return (
    <button data-testid={testid} onClick={() => onChange(!on)} role="switch" aria-checked={on}
      className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-gradient-violet" : "bg-navy/15"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function Automation() {
  const { t, lang } = useI18n();
  const [metrics, setMetrics] = useState(null);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [custom, setCustom] = useState([]);
  const [palette, setPalette] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/automation/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/automation/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/automation/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/automation/custom").then((r) => setCustom(r.data)).catch(() => {});
    api.get("/automation/palette").then((r) => setPalette(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (rule, enabled) => {
    setRules((rs) => rs.map((r) => (r.key === rule.key ? { ...r, enabled } : r)));
    try { await api.patch(`/automation/rules/${rule.key}`, { enabled }); api.get("/automation/overview").then((r) => setMetrics(r.data.metrics)); }
    catch (e) { toast.error("Error"); load(); }
  };
  const toggleCustom = async (rule, enabled) => {
    setCustom((rs) => rs.map((r) => (r.key === rule.key ? { ...r, enabled } : r)));
    try { await api.patch(`/automation/custom/${rule.key}`, { enabled }); api.get("/automation/overview").then((r) => setMetrics(r.data.metrics)); }
    catch (e) { toast.error("Error"); load(); }
  };
  const delCustom = async (key) => { await api.delete(`/automation/custom/${key}`); load(); };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "es-ES", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <OSLayout title={t("os.nav.automation")}>
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">OMNIA · Automation</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Everything Connected</h2>
              <p className="mt-2 text-navy/50">{t("os.automation.subtitle")}</p>
            </motion.div>
            <button data-testid="automation-new-rule" onClick={() => setBuilderOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /> {t("os.automation.newRule")}
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`automation-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Rules */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-lg font-medium">{t("os.automation.rulesTitle")}</h3>
              {rules.map((r, i) => (
                <motion.div key={r.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.05 }}
                  data-testid={`automation-rule-${r.key}`}
                  className="bg-white border border-navy/10 rounded-lg p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${r.enabled ? "bg-gradient-violet text-white" : "bg-navy/5 text-navy/30"}`}><Zap className="w-4 h-4" /></div>
                      <div>
                        <div className="font-medium">{lang === "en" ? r.name_en : r.name_es}</div>
                        <p className="text-sm text-navy/50 mt-0.5">{lang === "en" ? r.desc_en : r.desc_es}</p>
                      </div>
                    </div>
                    <Toggle on={r.enabled} onChange={(v) => toggle(r, v)} testid={`automation-toggle-${r.key}`} />
                  </div>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-navy/5 text-xs text-navy/40">
                    <span className="inline-flex items-center gap-1.5 bg-navy/5 rounded-full px-2.5 py-1">{lang === "en" ? r.trigger_en : r.trigger_es} <ArrowRight className="w-3 h-3" /></span>
                    <span className="ml-auto">{r.run_count} {t("os.automation.runs")}</span>
                  </div>
                </motion.div>
              ))}

              {/* Custom rules */}
              <div className="pt-4">
                <h3 className="text-lg font-medium mb-4">{t("os.automation.customTitle")}</h3>
                {custom.length === 0 ? (
                  <div className="text-navy/40 text-sm border border-dashed border-navy/15 rounded-lg py-8 text-center">{t("os.automation.customEmpty")}</div>
                ) : (
                  <div className="space-y-4">
                    {custom.map((r) => (
                      <div key={r.key} data-testid={`automation-custom-${r.key}`} className="bg-white border border-violet/20 rounded-lg p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${r.enabled ? "bg-gradient-violet text-white" : "bg-navy/5 text-navy/30"}`}><Zap className="w-4 h-4" /></div>
                            <div>
                              <div className="font-medium">{r.name}</div>
                              <p className="text-xs text-navy/50 mt-0.5">
                                SI {palette?.triggers?.find((tt) => tt.key === r.trigger?.type)?.[lang === "en" ? "label_en" : "label_es"]}
                                {r.trigger?.type === "lead_stage_changed" && r.trigger?.stage !== "any" ? ` = ${r.trigger.stage}` : ""}
                                {" → "}{palette?.actions?.find((a) => a.key === r.action?.type)?.[lang === "en" ? "label_en" : "label_es"]}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Toggle on={r.enabled} onChange={(v) => toggleCustom(r, v)} testid={`automation-custom-toggle-${r.key}`} />
                            <button data-testid={`automation-custom-del-${r.key}`} onClick={() => delCustom(r.key)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <div className="flex items-center mt-4 pt-4 border-t border-navy/5 text-xs text-navy/40">
                          <span className="ml-auto">{r.run_count} {t("os.automation.runs")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Activity log */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{t("os.automation.logsTitle")}</h3>
              <div className="bg-white border border-navy/10 rounded-lg p-4" data-testid="automation-logs-panel">
                {logs.length === 0 ? (
                  <div className="text-navy/40 text-sm py-8 text-center">{t("os.automation.empty")}</div>
                ) : (
                  <div className="space-y-4">
                    {logs.map((l) => (
                      <div key={l.id} data-testid={`automation-log-${l.id}`} className="flex gap-3">
                        <CheckCircle2 className="w-4 h-4 text-violet shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{l.title}</div>
                          <div className="text-xs text-navy/50 mt-0.5">{l.detail}</div>
                          <div className="text-[10px] text-navy/30 mt-1">{fmtDate(l.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {builderOpen && palette && <BuilderModal palette={palette} t={t} onClose={() => setBuilderOpen(false)}
        onCreated={() => { setBuilderOpen(false); load(); }} />}
    </OSLayout>
  );
}

function BuilderModal({ palette, onClose, onCreated, t }) {
  const [f, setF] = useState({ name: "", trigger_type: "lead_stage_changed", trigger_stage: "any", action_type: "log_only", action_email: "", action_subject: "", action_message: "", action_name: "" });
  const [err, setErr] = useState("");
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/automation/custom", f); toast.success("OK"); onCreated(); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); }
  };
  const label = (arr, key) => arr.find((x) => x.key === key);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="automation-builder-modal" className="w-full max-w-lg bg-cream rounded-xl p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{t("os.automation.builder.title")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <input required placeholder={t("os.automation.builder.name")} data-testid="automation-builder-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />

          <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-3">
            <div className="overline text-violet">{t("os.automation.builder.ifLabel")}</div>
            <select data-testid="automation-builder-trigger" value={f.trigger_type} onChange={(e) => set("trigger_type", e.target.value)} className={inputCls}>
              {palette.triggers.map((tr) => <option key={tr.key} value={tr.key}>{label(palette.triggers, tr.key).label_es}</option>)}
            </select>
            {f.trigger_type === "lead_stage_changed" && (
              <select data-testid="automation-builder-stage" value={f.trigger_stage} onChange={(e) => set("trigger_stage", e.target.value)} className={inputCls}>
                <option value="any">{t("os.automation.builder.anyStage")}</option>
                {palette.lead_stages.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>

          <div className="flex justify-center"><ArrowRight className="w-5 h-5 text-navy/30 rotate-90" /></div>

          <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-3">
            <div className="overline text-violet">{t("os.automation.builder.thenLabel")}</div>
            <select data-testid="automation-builder-action" value={f.action_type} onChange={(e) => set("action_type", e.target.value)} className={inputCls}>
              {palette.actions.map((a) => <option key={a.key} value={a.key}>{label(palette.actions, a.key).label_es}</option>)}
            </select>
            {f.action_type === "send_email" && (
              <>
                <input type="email" required placeholder={t("os.automation.builder.email")} data-testid="automation-builder-email" value={f.action_email} onChange={(e) => set("action_email", e.target.value)} className={inputCls} />
                <input placeholder={t("os.automation.builder.subject")} value={f.action_subject} onChange={(e) => set("action_subject", e.target.value)} className={inputCls} />
                <textarea rows={3} placeholder={t("os.automation.builder.message")} value={f.action_message} onChange={(e) => set("action_message", e.target.value)} className={`${inputCls} resize-none`} />
              </>
            )}
            {f.action_type === "create_project" && (
              <input placeholder={t("os.automation.builder.projectName")} value={f.action_name} onChange={(e) => set("action_name", e.target.value)} className={inputCls} />
            )}
            {f.action_type === "log_only" && (
              <input placeholder={t("os.automation.builder.message")} value={f.action_message} onChange={(e) => set("action_message", e.target.value)} className={inputCls} />
            )}
          </div>

          <p className="text-[11px] text-navy/40">{t("os.automation.builder.hint")}</p>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button data-testid="automation-builder-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.automation.builder.save")}</button>
        </form>
      </motion.div>
    </div>
  );
}
